import React, { useState, useEffect } from "react";
// DropZone removed
// import DropZone from "../data/DropZone";
import { useFormContext, useWatch } from "react-hook-form";
import { useTabContext } from "../context/context";
// LogoUpload removed if you no longer need it
// import LogoUpload from "../components/LogoUpload";
// Removed GalleryUpload import
import { FaFilePdf } from "react-icons/fa";
import { FaX } from "react-icons/fa6";
import axios from "axios";

/**
 * MediaUploads Component - Handles all media-related uploads for a provider's profile
 * including logo, gallery images, brochure, and floor plans.
 */
const MediaUploads = ({ isPremium, setPremiumChangesMade }) => {
  const { register, setValue, getValues } = useFormContext();
  const { companyLogoURL, setCompanyLogoURL, brochureURL, setBrochureURL, floorPlanURL, setFloorPlanURL, premiumInfo, setUploadError } = useTabContext();

  // Local state for various media
  const [galleryImages, setGalleryImages] = useState([]);
  const [companyLogo, setCompanyLogo] = useState({});
  const [newGalleryImages, setNewGalleryImages] = useState([]);
  const [maxFiles, setMaxFiles] = useState(5);
  const [brochure, setBrochure] = useState({});
  const [floorPlan, setFloorPlan] = useState({});
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  useEffect(() => {
    if (premiumInfo) {
      // Company Logo
      if (premiumInfo.logo) {
        setCompanyLogo({
          type: "image",
          url: premiumInfo.logo
        });
      }

      // Gallery
      if (premiumInfo.gallery) {
        setGalleryImages(
          premiumInfo.gallery.map(image => ({
            id: image.id,
            url: image.url
          }))
        );
      }

      // Brochure
      if (premiumInfo.brochure) {
        setBrochure({
          type: premiumInfo.brochure_type,
          url: premiumInfo.brochure
        });
      }

      // Floor Plan
      if (premiumInfo.floorplan) {
        setFloorPlan({
          type: premiumInfo.floorplan_type,
          url: premiumInfo.floorplan
        });
      }
    }
  }, [premiumInfo]);

  /**
   * Handles brochure file upload
   */
  const handleBrochureUpload = files => {
    if (files?.length > 0) {
      const uploadedBrochure = files[0];
      const reader = new FileReader();

      reader.onload = () => {
        setBrochure({
          type: uploadedBrochure.type === "application/pdf" ? "pdf" : "image",
          url: reader.result,
          name: uploadedBrochure.name
        });
        setValue("premium.company_brochure", [uploadedBrochure]);
        setBrochureURL(reader.result);
      };

      setPremiumChangesMade(true);
      reader.readAsDataURL(uploadedBrochure);
    }
  };

  /**
   * Handles floor plan file upload
   */
  const handleFloorPlanUpload = files => {
    if (files?.length > 0) {
      const uploadedFloorPlan = files[0];
      const reader = new FileReader();

      reader.onload = () => {
        setFloorPlan({
          type: uploadedFloorPlan.type === "application/pdf" ? "pdf" : "image",
          url: reader.result,
          name: uploadedFloorPlan.name
        });
        setValue("premium.floor_plan", [uploadedFloorPlan]);
        setFloorPlanURL(reader.result);
      };

      setPremiumChangesMade(true);
      reader.readAsDataURL(uploadedFloorPlan);
    }
  };

  /**
   * Handles company logo upload (single file)
   */
  const handleLogoUpload = files => {
    if (files?.length > 0) {
      const uploadedLogo = files[0];
      console.log("Selected file:", uploadedLogo.name);
      console.log(uploadedLogo);
      setValue("premium.company_logo", uploadedLogo);
      const reader = new FileReader();

      reader.onload = () => {
        setCompanyLogo({
          type: uploadedLogo.type === "application/pdf" ? "pdf" : "image",
          url: reader.result,
          name: uploadedLogo.name
        });
        // Update react-hook-form and context

        setCompanyLogoURL(reader.result);
        console.log("Company logo is set in state and React Hook Form.");
      };

      setPremiumChangesMade(true);
      reader.readAsDataURL(uploadedLogo);
    }
  };

  /**
   * Handles gallery image uploads (max of 5)
   */
  const handleImageUpload = files => {
    if (!files?.length) return;

    const remainingSlots = maxFiles - galleryImages.length;
    if (remainingSlots <= 0) return;

    const newImageObjects = [];
    const limitedFiles = Array.from(files).slice(0, remainingSlots);

    limitedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        newImageObjects.push({
          id: `${Date.now()}-${file.name}`,
          url: reader.result,
          file: file
        });

        // When all are processed
        if (newImageObjects.length === limitedFiles.length) {
          // 1) Update local preview state
          setGalleryImages(prev => {
            return [...prev, ...newImageObjects].slice(0, maxFiles);
          });

          // 2) Get the current full_gallery array from RHF
          const currentGallery = getValues("premium.full_gallery") || [];

          // 3) Combine them
          const combined = [...currentGallery, ...newImageObjects].slice(0, maxFiles);

          // 4) Store that final array
          setValue("premium.full_gallery", combined);
        }
      };
      setPremiumChangesMade(true);
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handle single media deletion (logo, brochure, floorplan) in local state + server
   */
  const handleDeleteMedia = type => {
    switch (type) {
      case "logo":
        setCompanyLogo({});
        setCompanyLogoURL("");
        setValue("premium.company_logo", null);
        break;
      case "brochure":
        setBrochure({});
        setBrochureURL("");
        setValue("premium.company_brochure", null);
        break;
      case "floorplan":
        setFloorPlan({});
        setFloorPlanURL("");
        setValue("premium.floor_plan", null);
        break;
      default:
        break;
    }
  };

  /**
   * Delete media file on the server (logo, brochure, floorplan)
   */
  const deleteMediaPost = async type => {
    try {
      const response = await axios.post(
        "/wp-json/provider/v1/delete-media/",
        {
          type: type,
          provID: premiumInfo.ID
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-WP-Nonce": window.userInfo.nonce
          }
        }
      );

      if (response.status === 200) {
        handleDeleteMedia(type);
      }
    } catch (error) {
      console.error("Error deleting media", error);
      if (setUploadError) {
        const backendMessage = error.response?.data?.message || "Error deleting media.";
        setUploadError(backendMessage);
      }
    }
  };

  /**
   * Remove a single gallery image (local state only)
   */
  const handleImageRemove = id => {
    // 1) Remove from local preview
    setGalleryImages(prev => {
      const updated = prev.filter(img => img.id !== id);
      setValue("premium.full_gallery", updated);
      return updated;
    });

    // 2) If it’s a brand-new file (no real DB ID), remove it from the “files to be uploaded”
    setNewGalleryImages(prev => {
      const filtered = prev.filter(fileObj => !id.includes(fileObj.file.name));
      // Or however you want to match them
      return filtered;
    });

    const existing = getValues("premium.deleted_image_ids") || [];
    setValue("premium.deleted_image_ids", [...existing, id]);
  };

  return (
    <div className="provider-section">
      <h3>Media</h3>

      <div className="mt-4 grid grid-cols-1 items-center gap-4 pt-4">
        {/* =======================
            COMPANY LOGO SECTION
        ======================= */}
        <div className="media-container">
          <h3>Company Logo:</h3>
          <div className="text-xs py-1">Only accepts JPG or PNG</div>

          {/* Preview if a logo is already uploaded */}
          {companyLogo?.url && (
            <div className="w-full mx-auto p-2 relative">
              {companyLogo.type === "pdf" ? (
                <div className="flex items-center">
                  <FaFilePdf className="text-xl mr-2 text-primaryBlue" />
                  <a href={companyLogo.url} target="_blank" rel="noreferrer">
                    {companyLogo.name || "View Company Logo"}
                  </a>
                </div>
              ) : (
                <img src={companyLogo.url} alt="Company Logo" className="w-full" />
              )}
              <button onClick={() => deleteMediaPost("logo")} className="absolute top-2 right-2 text-white bg-red-500 rounded-full p-1">
                <FaX />
              </button>
            </div>
          )}

          <div className="ca-upload-box">
            <label>
              <input
                type="file"
                accept="image/png, image/jpeg, application/pdf"
                className="
                  w-full border border-primaryBlue rounded-lg border-dashed                
                  file:!rounded-lg
                  file:!ml-0
                  file:!bg-primaryBlue
                  file:!hover:bg-primaryBlue/80
                  file:!text-white
                  file:!hover:text-white
                "
                // Connect this file input to react-hook-form

                onChange={e => {
                  handleLogoUpload(e.target.files);
                }}
              />
            </label>
          </div>
        </div>

        {/* =======================
            GALLERY SECTION
        ======================= */}
        <div className="media-container">
          <h3>Gallery Images (max 5):</h3>
          <div className="text-xs py-1">Only accepts JPG or PNG</div>

          {galleryImages?.length > 0 && (
            <>
              {/* If there's only 1 image => full width, no grid */}
              {galleryImages.length === 1 ? (
                <div className="w-full">
                  {galleryImages.map(image => (
                    <div key={image.id} className="relative border p-1">
                      <img src={image.url} alt="Gallery" className="w-full h-auto" />
                      <button type="button" onClick={() => handleImageRemove(image.id)} className="absolute top-2 right-2 text-white bg-red-500 rounded-full p-1">
                        <FaX />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                /* If there's >= 2 images => grid with 2 columns */
                <div className="grid grid-cols-2 gap-2 my-2">
                  {galleryImages.map((image, idx) => (
                    <div
                      key={image.id}
                      /* 
              If exactly 5 images, 
              and this is the first one (idx===0), 
              make it span 2 columns 
            */
                      className={`relative border p-1 ${galleryImages.length === 5 && idx === 0 ? "col-span-2" : ""}`}
                    >
                      <img src={image.url} alt="Gallery" className="w-full h-auto" />
                      <button type="button" onClick={() => handleImageRemove(image.id)} className="absolute top-2 right-2 text-white bg-red-500 rounded-full p-1">
                        <FaX />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Upload input for new gallery images */}
          {galleryImages.length < maxFiles && (
            <div className="ca-upload-box">
              <label>
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                  className="
                    w-full border border-primaryBlue rounded-lg border-dashed                
                    file:!rounded-lg
                    file:!ml-0
                    file:!bg-primaryBlue
                    file:!hover:bg-primaryBlue/80
                    file:!text-white
                    file:!hover:text-white
                  "
                  onChange={e => {
                    handleImageUpload(e.target.files);
                  }}
                />
              </label>
            </div>
          )}
        </div>

        {/* =======================
            BROCHURE SECTION
        ======================= */}
        <div className="media-container">
          <h3>Company Brochure:</h3>
          <div className="text-xs py-1 relative">Only accepts PDF, JPG, or PNG</div>

          {brochure?.url && (
            <div className="w-full mx-auto p-2 relative">
              {brochure.type === "pdf" ? (
                <div className="flex items-center">
                  <FaFilePdf className="text-xl mr-2 text-primaryBlue" />
                  <a href={brochure.url} target="_blank" rel="noreferrer">
                    {brochure.name || "View Brochure"}
                  </a>
                </div>
              ) : (
                <img src={brochure.url} alt="Company Brochure" className="w-full" />
              )}
              <button onClick={() => deleteMediaPost("brochure")} className="absolute top-2 right-2 text-white bg-red-500 rounded-full p-1">
                <FaX />
              </button>
            </div>
          )}
          <div className="ca-upload-box">
            <label>
              <input
                type="file"
                accept="image/png, image/jpeg, application/pdf"
                className="
                  w-full border border-primaryBlue rounded-lg border-dashed                
                  file:!rounded-lg
                  file:!ml-0
                  file:!bg-primaryBlue
                  file:!hover:bg-primaryBlue/80
                  file:!text-white
                  file:!hover:text-white
                "
                // Connect this file input to react-hook-form

                onChange={e => {
                  handleBrochureUpload(e.target.files);
                  setValue("premium.company_brochure", e.target.files[0]);
                }}
              />
            </label>
          </div>
        </div>

        {/* =======================
            FLOOR PLAN SECTION
        ======================= */}
        <div className="media-container">
          <h3>Community Floor Plan:</h3>
          <div className="text-xs py-1">Only accepts PDF, JPG, or PNG</div>

          {floorPlan?.url && (
            <div className="w-full mx-auto p-2 relative">
              {floorPlan.type === "pdf" ? (
                <div className="flex items-center">
                  <FaFilePdf className="text-xl mr-2 text-primaryBlue" />
                  <a href={floorPlan.url} target="_blank" rel="noreferrer">
                    {floorPlan.name || "View Floor Plan"}
                  </a>
                </div>
              ) : (
                <img src={floorPlan.url} alt="Company Floor Plan" className="w-full" />
              )}
              <button onClick={() => deleteMediaPost("floorplan")} className="absolute top-2 right-2 text-white bg-red-500 rounded-full p-1">
                <FaX />
              </button>
            </div>
          )}
          <div className="ca-upload-box">
            <label>
              <input
                type="file"
                accept="image/png, image/jpeg, application/pdf"
                className="
                  w-full border border-primaryBlue rounded-lg border-dashed                
                  file:!rounded-lg
                  file:!ml-0
                  file:!bg-primaryBlue
                  file:!hover:bg-primaryBlue/80
                  file:!text-white
                  file:!hover:text-white
                "
                // Connect this file input to react-hook-form

                onChange={e => {
                  handleFloorPlanUpload(e.target.files);
                  setValue("premium.floor_plan", e.target.files[0]);
                }}
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaUploads;
